import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Circle,
  Image,
  MapPin,
  Minus,
  MousePointer2,
  Palette,
  Plus,
  RectangleHorizontal,
  Scan,
  SlidersHorizontal,
  Square,
  Thermometer,
  Trash2,
} from "lucide-react";
import { useAppStore } from "lib/store/useAppStore";
import SingleImageDownloadBtn from "components/project/SingleImageDownloadBtn";
import { PaletteOptionsList } from "pages/files/image/thermal/ThermalPanel";
import {
  getCreateInspectionResultPref,
  setCreateInspectionResultPref,
} from "lib/utils/inspectionResultGenerationPref";

const EMPTY_VALUE = "-";
const POINT_MIN = 3;
const POINT_MAX = 9;
const AREA_MAX = 5;
const MODE_POINT = "point";
const MODE_AREA = "area";
const AREA_SHAPES = ["rectangle", "square", "circle"];
const MARKUP_POINT_COLOR = "#ffffff";
const MARKUP_TEXT_COLOR = "#ffffff";
const MARKUP_TEXT_STROKE_COLOR = "#000000";
const MARKUP_AREA_COLOR = "#2f766f";
const POINT_LABEL_VERTICAL_OFFSET = 24;
const POINT_LABEL_EDGE_PADDING = 8;
const AREA_SHAPE_ICONS = {
  rectangle: RectangleHorizontal,
  square: Square,
  circle: Circle,
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function clampIndex(index, max) {
  if (!Number.isFinite(index) || max <= 0) return -1;
  return Math.min(Math.max(index, 0), max - 1);
}

function getThreePhaseSize(csvOrigin) {
  const height = Array.isArray(csvOrigin) ? csvOrigin.length : 0;
  const width = Array.isArray(csvOrigin?.[0]) ? csvOrigin[0].length : 0;

  return width > 0 && height > 0
    ? { width, height }
    : { width: 640, height: 435 };
}

function hasPointValue(point) {
  return (
    point?.value !== null && point?.value !== undefined && point?.value !== ""
  );
}

function parsePointValue(point) {
  const numericValue = Number(
    String(point?.value ?? "").replace(/[^\d.-]/g, ""),
  );
  return Number.isFinite(numericValue) ? numericValue : null;
}

function formatTemperature(value) {
  if (!Number.isFinite(value)) return EMPTY_VALUE;
  return `${value.toFixed(1)} °C`;
}

function formatAreaStatsTemperature(value) {
  if (!Number.isFinite(value)) return EMPTY_VALUE;
  return value.toFixed(1);
}

function shouldPlacePointLabelBelow(y, offset = POINT_LABEL_VERTICAL_OFFSET) {
  return y - offset < POINT_LABEL_EDGE_PADDING;
}

export function getPointRemoveButtonPosition(point, editorSize) {
  const size = 28;
  const padding = 6;
  // At vertical edges the button is clamped inward; keep it beside the label too.
  const labelWidth = Math.max(62, `L1: ${point.value}`.length * 8 + 12);
  const offset = point.y < size / 2 + padding || point.y > editorSize.height - size / 2 - padding
    ? Math.max(23, labelWidth / 2 + padding)
    : 23;
  const candidates = [
    { x: point.x + offset, y: point.y - size / 2 },
    { x: point.x - offset - size, y: point.y - size / 2 },
    { x: point.x - size / 2, y: point.y + offset },
    { x: point.x - size / 2, y: point.y - offset - size },
  ];
  const maxX = Math.max(padding, editorSize.width - size - padding);
  const maxY = Math.max(padding, editorSize.height - size - padding);
  const position = candidates.find(({ x, y }) =>
    x >= padding && x <= maxX && y >= padding && y <= maxY,
  ) ?? candidates.find(({ x }) => x >= padding && x <= maxX) ?? candidates[0];

  return {
    left: clamp(position.x, padding, maxX) - point.x + 15,
    top: clamp(position.y, padding, maxY) - point.y + 15,
  };
}

function normalizePointSlots(points, pointLimit) {
  const next = Array.from(
    { length: pointLimit },
    (_, index) => points?.[index],
  );
  const isSameLength = Array.isArray(points) && points.length === pointLimit;
  const isSameContent =
    isSameLength && next.every((point, index) => point === points[index]);

  return isSameContent ? points : next;
}

function compactPointSlots(points, pointLimit) {
  const normalized = normalizePointSlots(points, pointLimit);
  const compacted = normalized.filter(Boolean);
  return normalizePointSlots(compacted, pointLimit);
}

function parseStoredArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function toFiniteNumber(value) {
  if (value === null || value === undefined) return null;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const numericValue = Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(numericValue) ? numericValue : null;
}

function normalizeSavedClickPoints(clickPoints) {
  return parseStoredArray(clickPoints)
    .map((point) => {
      const x = toFiniteNumber(point?.x);
      const y = toFiniteNumber(point?.y);

      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

      return {
        ...point,
        x,
        y,
        value: point?.value ?? point?.temperature ?? point?.temp ?? "",
      };
    })
    .filter(Boolean)
    .slice(0, POINT_MAX);
}

function parseStoredAreaCoords(coords) {
  if (typeof coords === "string") {
    const trimmed = coords.trim();
    if (!trimmed) return null;

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      const values = trimmed
        .split(/[,\s]+/)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

      if (values.length >= 4) {
        return {
          x: values[0],
          y: values[1],
          width: values[2],
          height: values[3],
        };
      }
    }

    return null;
  }

  return coords && typeof coords === "object" ? coords : null;
}

function normalizeStoredAreaStats(area) {
  return {
    max: toFiniteNumber(area?.max ?? area?.stats?.max),
    min: toFiniteNumber(area?.min ?? area?.stats?.min),
    avg: toFiniteNumber(area?.avg ?? area?.stats?.avg),
    count: toFiniteNumber(area?.count ?? area?.stats?.count) ?? 0,
  };
}

function restoreClickAreas(clickAreas, displaySize, sourceSize) {
  const sourceWidth = Number(sourceSize?.width) || displaySize.width;
  const sourceHeight = Number(sourceSize?.height) || displaySize.height;
  const scaleX = displaySize.width / sourceWidth;
  const scaleY = displaySize.height / sourceHeight;

  return parseStoredArray(clickAreas)
    .map((area, index) => {
      const coords = parseStoredAreaCoords(area?.coords ?? area);
      const sourceX = toFiniteNumber(coords?.x);
      const sourceY = toFiniteNumber(coords?.y);
      const sourceWidthValue = toFiniteNumber(coords?.width);
      const sourceHeightValue = toFiniteNumber(coords?.height);

      if (
        !Number.isFinite(sourceX) ||
        !Number.isFinite(sourceY) ||
        !Number.isFinite(sourceWidthValue) ||
        !Number.isFinite(sourceHeightValue) ||
        sourceWidthValue <= 0 ||
        sourceHeightValue <= 0
      ) {
        return null;
      }

      const x = clamp(sourceX * scaleX, 0, displaySize.width);
      const y = clamp(sourceY * scaleY, 0, displaySize.height);
      const width = clamp(sourceWidthValue * scaleX, 0, displaySize.width - x);
      const height = clamp(
        sourceHeightValue * scaleY,
        0,
        displaySize.height - y,
      );

      if (width <= 0 || height <= 0) return null;

      return {
        id: `saved-${index}-${sourceX}-${sourceY}`,
        shape: AREA_SHAPES.includes(area?.shape) ? area.shape : "rectangle",
        x,
        y,
        width,
        height,
        stats: normalizeStoredAreaStats(area),
      };
    })
    .filter(Boolean)
    .slice(0, AREA_MAX);
}

function buildPointRows(circles, pointLimit) {
  return Array.from({ length: pointLimit }, (_, index) => {
    const point = circles?.[index];
    const numericValue = parsePointValue(point);

    return {
      key: `L${index + 1}`,
      label: `L${index + 1}`,
      value: numericValue,
      displayValue: Number.isFinite(numericValue)
        ? formatTemperature(numericValue)
        : EMPTY_VALUE,
      position:
        point?.x !== undefined && point?.y !== undefined
          ? `${Math.round(point.x)}, ${Math.round(point.y)}`
          : EMPTY_VALUE,
      isSelected: Boolean(point && hasPointValue(point)),
    };
  });
}

function toSourcePoint(x, y, displaySize, sourceSize) {
  return {
    x: x * (sourceSize.width / displaySize.width),
    y: y * (sourceSize.height / displaySize.height),
  };
}

function toDisplayPoint(x, y, displaySize, sourceSize) {
  return {
    x: x * (displaySize.width / sourceSize.width),
    y: y * (displaySize.height / sourceSize.height),
  };
}

function getCsvPointFromClick(csvOrigin, x, y, file, displaySize, sourceSize) {
  const rowCount = Array.isArray(csvOrigin) ? csvOrigin.length : 0;
  const colCount =
    rowCount > 0 && Array.isArray(csvOrigin[0]) ? csvOrigin[0].length : 0;

  if (rowCount <= 0 || colCount <= 0) {
    return { rowIndex: -1, colIndex: -1 };
  }

  const sourcePoint = toSourcePoint(x, y, displaySize, sourceSize);

  if (rowCount === 512) {
    const thermalOffsetX = Number(file?.thermal_offset_x) || 0;
    const thermalOffsetY = Number(file?.thermal_offset_y) || 0;
    const scale = 1.25;

    return {
      rowIndex: clampIndex(
        Math.floor((sourcePoint.y - thermalOffsetY) / scale),
        rowCount,
      ),
      colIndex: clampIndex(
        Math.floor((sourcePoint.x - thermalOffsetX) / scale),
        colCount,
      ),
    };
  }

  return {
    rowIndex: clampIndex(Math.floor(sourcePoint.y), rowCount),
    colIndex: clampIndex(Math.floor(sourcePoint.x), colCount),
  };
}

function getCsvTemperature(file, x, y, displaySize, sourceSize) {
  if (!Array.isArray(file?.csv_origin)) return null;

  const { rowIndex, colIndex } = getCsvPointFromClick(
    file.csv_origin,
    x,
    y,
    file,
    displaySize,
    sourceSize,
  );
  const row = file.csv_origin[rowIndex];
  const rawValue = row?.[colIndex];

  return typeof rawValue === "number" && Number.isFinite(rawValue)
    ? rawValue
    : null;
}

export function moveThreePhasePoint(point, dx, dy, file, displaySize, sourceSize) {
  if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) return point;

  const csv = file?.csv_origin;
  const { rowIndex, colIndex } = getCsvPointFromClick(
    csv, point.x, point.y, file, displaySize, sourceSize,
  );
  if (rowIndex < 0 || colIndex < 0) return point;

  const row = rowIndex + dy;
  const col = colIndex + dx;
  const value = csv[row]?.[col];
  if (row < 0 || col < 0 || typeof value !== "number" || !Number.isFinite(value)) {
    return point;
  }

  const position = getScreenPointFromCsv(row, col, file, displaySize, sourceSize);
  const x = dx ? position.x : point.x;
  const y = dy ? position.y : point.y;
  if (x < 0 || y < 0 || x >= displaySize.width || y >= displaySize.height) {
    return point;
  }

  return { ...point, x, y, value: value.toFixed(1) };
}

function getPointerPosition(event, editorSize) {
  return getPointerPositionFromElement(event, event.currentTarget, editorSize);
}

function getPointerPositionFromElement(event, element, editorSize) {
  const rect = element.getBoundingClientRect();

  // Keep pointer coordinates aligned with the CSV grid, including browser zoom.
  return {
    x: (event.clientX - rect.left) * (editorSize.width / (rect.width || editorSize.width)),
    y: (event.clientY - rect.top) * (editorSize.height / (rect.height || editorSize.height)),
  };
}

function normalizeArea(startX, startY, currentX, currentY, shape, editorSize) {
  let endX = currentX;
  let endY = currentY;

  if (shape === "square" || shape === "circle") {
    const directionX = currentX >= startX ? 1 : -1;
    const directionY = currentY >= startY ? 1 : -1;
    const side = Math.max(
      Math.abs(currentX - startX),
      Math.abs(currentY - startY),
    );

    endX = startX + side * directionX;
    endY = startY + side * directionY;
  }

  const left = clamp(Math.min(startX, endX), 0, editorSize.width);
  const top = clamp(Math.min(startY, endY), 0, editorSize.height);
  const right = clamp(Math.max(startX, endX), 0, editorSize.width);
  const bottom = clamp(Math.max(startY, endY), 0, editorSize.height);

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
    shape,
  };
}

function getScreenPointFromCsv(
  rowIndex,
  colIndex,
  file,
  displaySize,
  sourceSize,
) {
  const rowCount = Array.isArray(file?.csv_origin) ? file.csv_origin.length : 0;
  let sourcePoint;

  if (rowCount === 512) {
    const thermalOffsetX = Number(file?.thermal_offset_x) || 0;
    const thermalOffsetY = Number(file?.thermal_offset_y) || 0;
    const scale = 1.25;

    sourcePoint = {
      x: colIndex * scale + thermalOffsetX + scale / 2,
      y: rowIndex * scale + thermalOffsetY + scale / 2,
    };
  } else {
    sourcePoint = {
      x: colIndex + 0.5,
      y: rowIndex + 0.5,
    };
  }

  return toDisplayPoint(sourcePoint.x, sourcePoint.y, displaySize, sourceSize);
}

function isPointInsideArea(area, x, y) {
  if (
    x < area.x ||
    x > area.x + area.width ||
    y < area.y ||
    y > area.y + area.height
  ) {
    return false;
  }

  if (area.shape !== "circle") return true;

  const radius = Math.min(area.width, area.height) / 2;
  const centerX = area.x + area.width / 2;
  const centerY = area.y + area.height / 2;
  const dx = x - centerX;
  const dy = y - centerY;

  return dx * dx + dy * dy <= radius * radius;
}

function calculateAreaStats(file, area, displaySize, sourceSize) {
  const csvOrigin = file?.csv_origin;
  const rowCount = Array.isArray(csvOrigin) ? csvOrigin.length : 0;
  const colCount =
    rowCount > 0 && Array.isArray(csvOrigin[0]) ? csvOrigin[0].length : 0;

  if (rowCount <= 0 || colCount <= 0) return null;

  const start = getCsvPointFromClick(
    csvOrigin,
    area.x,
    area.y,
    file,
    displaySize,
    sourceSize,
  );
  const end = getCsvPointFromClick(
    csvOrigin,
    area.x + area.width,
    area.y + area.height,
    file,
    displaySize,
    sourceSize,
  );
  const rowStart = clampIndex(
    Math.min(start.rowIndex, end.rowIndex) - 2,
    rowCount,
  );
  const rowEnd = clampIndex(
    Math.max(start.rowIndex, end.rowIndex) + 2,
    rowCount,
  );
  const colStart = clampIndex(
    Math.min(start.colIndex, end.colIndex) - 2,
    colCount,
  );
  const colEnd = clampIndex(
    Math.max(start.colIndex, end.colIndex) + 2,
    colCount,
  );
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let count = 0;

  for (let rowIndex = rowStart; rowIndex <= rowEnd; rowIndex += 1) {
    for (let colIndex = colStart; colIndex <= colEnd; colIndex += 1) {
      const rawValue = csvOrigin[rowIndex]?.[colIndex];

      if (typeof rawValue !== "number" || !Number.isFinite(rawValue)) continue;

      const screenPoint = getScreenPointFromCsv(
        rowIndex,
        colIndex,
        file,
        displaySize,
        sourceSize,
      );

      if (!isPointInsideArea(area, screenPoint.x, screenPoint.y)) continue;

      min = Math.min(min, rawValue);
      max = Math.max(max, rawValue);
      sum += rawValue;
      count += 1;
    }
  }

  if (count === 0) return null;

  return {
    min,
    max,
    avg: sum / count,
    count,
  };
}

function getAreaLabelStyle(area, editorSize) {
  const areaCenterX = area.x + area.width / 2;
  const isRightSide = areaCenterX > editorSize.width / 2;
  const horizontalOffset = 14;
  const verticalOffset = 2;
  const estimatedLabelHeight = 74;

  return {
    left: isRightSide
      ? area.x - horizontalOffset
      : area.x + area.width + horizontalOffset,
    top: clamp(
      area.y + verticalOffset,
      verticalOffset,
      Math.max(verticalOffset, editorSize.height - estimatedLabelHeight),
    ),
    transform: `translateX(${isRightSide ? "-100%" : "0"})`,
  };
}

function getAreaLabelClassName(area, editorSize) {
  const areaCenterX = area.x + area.width / 2;
  const isRightSide = areaCenterX > editorSize.width / 2;

  return isRightSide ? "is-label-left" : "is-label-right";
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("Image source is empty."));
      return;
    }

    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image."));
    image.src = src;
  });
}

function drawOutlinedText(ctx, text, x, y, options = {}) {
  const {
    align = "left",
    baseline = "top",
    fontSize = 13,
    lineWidth = 4,
  } = options;

  ctx.save();
  ctx.font = `700 ${fontSize}px sans-serif`;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  ctx.lineJoin = "round";
  ctx.strokeStyle = MARKUP_TEXT_STROKE_COLOR;
  ctx.lineWidth = lineWidth;
  ctx.fillStyle = MARKUP_TEXT_COLOR;
  ctx.strokeText(text, x, y);
  ctx.fillText(text, x, y);
  ctx.restore();
}

function drawMarkupPoint(ctx, point, index, scaleX, scaleY) {
  if (!point || !hasPointValue(point)) return;

  // x, y 좌표 유효성 확인
  const x = Number(point?.x);
  const y = Number(point?.y);

  if (!Number.isFinite(x) || !Number.isFinite(y)) return;

  const scale = (scaleX + scaleY) / 2;
  const scaledX = x * scaleX;
  const scaledY = y * scaleY;
  const radius = 15 * scale;
  const label = `L${index + 1}: ${point.value}`;
  const labelOffset = POINT_LABEL_VERTICAL_OFFSET * scale;
  const labelY = shouldPlacePointLabelBelow(scaledY, labelOffset)
    ? scaledY + labelOffset
    : scaledY - labelOffset;

  ctx.save();
  ctx.beginPath();
  ctx.arc(scaledX, scaledY, radius, 0, Math.PI * 2);
  ctx.strokeStyle = MARKUP_POINT_COLOR;
  ctx.lineWidth = Math.max(2, 2 * scale);
  ctx.stroke();
  ctx.restore();

  drawOutlinedText(ctx, label, scaledX, labelY, {
    align: "center",
    baseline: "middle",
    fontSize: Math.max(13, 13 * scale),
    lineWidth: Math.max(4, 4 * scale),
  });
}

function drawMarkupAreaShape(ctx, area, scaleX, scaleY) {
  const scale = (scaleX + scaleY) / 2;
  const x = area.x * scaleX;
  const y = area.y * scaleY;
  const width = area.width * scaleX;
  const height = area.height * scaleY;

  ctx.save();
  ctx.strokeStyle = MARKUP_AREA_COLOR;
  ctx.lineWidth = Math.max(2, 2 * scale);

  if (area.shape === "circle") {
    ctx.beginPath();
    ctx.ellipse(
      x + width / 2,
      y + height / 2,
      Math.abs(width / 2),
      Math.abs(height / 2),
      0,
      0,
      Math.PI * 2,
    );
    ctx.stroke();
  } else {
    ctx.strokeRect(x, y, width, height);
  }

  ctx.restore();
}

function drawMarkupAreaStats(ctx, area, index, displaySize, scaleX, scaleY) {
  const scale = (scaleX + scaleY) / 2;
  const isLabelLeft = area.x + area.width / 2 > displaySize.width / 2;
  const labelBaseX = isLabelLeft ? area.x - 14 : area.x + area.width + 14;
  const labelY = clamp(area.y + 2, 2, Math.max(2, displaySize.height - 74));
  const x = labelBaseX * scaleX;
  const y = labelY * scaleY;
  const align = isLabelLeft ? "right" : "left";
  const lineGap = 16 * scale;
  const fontSize = Math.max(11, 11 * scale);
  const titleFontSize = Math.max(12, 12 * scale);
  const strokeWidth = Math.max(4, 4 * scale);
  const connectorLength = 12 * scale;
  const connectorOffset = 2 * scale;

  ctx.save();
  ctx.strokeStyle = MARKUP_POINT_COLOR;
  ctx.lineWidth = Math.max(2, 2 * scale);
  ctx.beginPath();
  if (isLabelLeft) {
    ctx.moveTo(x + connectorOffset, y + 6 * scale);
    ctx.lineTo(x + connectorOffset + connectorLength, y + 6 * scale);
  } else {
    ctx.moveTo(x - connectorOffset, y + 6 * scale);
    ctx.lineTo(x - connectorOffset - connectorLength, y + 6 * scale);
  }
  ctx.stroke();
  ctx.restore();

  drawOutlinedText(ctx, `A${index + 1}`, x, y, {
    align,
    fontSize: titleFontSize,
    lineWidth: strokeWidth,
  });
  drawOutlinedText(
    ctx,
    `Max ${formatAreaStatsTemperature(area.stats?.max)}`,
    x,
    y + lineGap,
    { align, fontSize, lineWidth: strokeWidth },
  );
  drawOutlinedText(
    ctx,
    `Min ${formatAreaStatsTemperature(area.stats?.min)}`,
    x,
    y + lineGap * 2,
    { align, fontSize, lineWidth: strokeWidth },
  );
  drawOutlinedText(
    ctx,
    `Avg ${formatAreaStatsTemperature(area.stats?.avg)}`,
    x,
    y + lineGap * 3,
    { align, fontSize, lineWidth: strokeWidth },
  );
}

function buildClickAreas(areas, scaleX, scaleY) {
  return (areas || []).map((area) => ({
    shape: area.shape,
    coords: {
      x: Number((area.x * scaleX).toFixed(2)),
      y: Number((area.y * scaleY).toFixed(2)),
      width: Number((area.width * scaleX).toFixed(2)),
      height: Number((area.height * scaleY).toFixed(2)),
    },
    max: formatAreaStatsTemperature(area.stats?.max),
    min: formatAreaStatsTemperature(area.stats?.min),
    avg: formatAreaStatsTemperature(area.stats?.avg),
  }));
}

async function captureThreePhaseMarkupImage({
  imageSrc,
  circles,
  areas,
  displaySize,
}) {
  const image = await loadImageElement(imageSrc);
  const width = image.naturalWidth || image.width || displaySize.width;
  const height = image.naturalHeight || image.height || displaySize.height;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(image, 0, 0, width, height);

  const scaleX = width / displaySize.width;
  const scaleY = height / displaySize.height;
  // Persist CSV-grid coordinates even when the downloaded image size differs.
  const clickAreas = buildClickAreas(areas, 1, 1);

  (areas || []).forEach((area, index) => {
    drawMarkupAreaShape(ctx, area, scaleX, scaleY);
    drawMarkupAreaStats(ctx, area, index, displaySize, scaleX, scaleY);
  });

  (circles || []).forEach((point, index) => {
    drawMarkupPoint(ctx, point, index, scaleX, scaleY);
  });

  return {
    dataUrl: canvas.toDataURL("image/png"),
    clickAreas,
  };
}

export default function ThreePhasePanel({
  file,
  circles,
  setCircles,
  currentPaletteOptionNumber,
  setCurrentPaletteOptionNumber,
  tripleSpotCaptureTarget,
  currentThermalImg,
  setCurrentThermalImg,
  imgChangeLoading,
  setImgChangeLoading,
  markupCaptureRef,
  onAreaCountChange,
  showSaveThermalButton = false,
  onSaveThermalInfo = null,
  saveThermalButtonLabel = "",
  t,
}) {
  const [changing, setChanging] = useState(false);
  const [pointLimit, setPointLimit] = useState(POINT_MIN);
  const [mode, setMode] = useState(MODE_POINT);
  const [areaShape, setAreaShape] = useState("rectangle");
  const [areas, setAreas] = useState([]);
  const [shouldCreateInspectionResult, setShouldCreateInspectionResult] =
    useState(() => getCreateInspectionResultPref());
  const showAlert = useAppStore((s) => s.showAlert);
  const sourceEditorSize = useMemo(
    () => getThreePhaseSize(file?.csv_origin),
    [file?.csv_origin],
  );
  const imageSrc = currentThermalImg || file?.image_thermal;
  const editorSize = sourceEditorSize;
  const pointRows = buildPointRows(circles, pointLimit);
  const pointValues = pointRows
    .map((point) => point.value)
    .filter((value) => Number.isFinite(value));
  const isDefaultZeroSet =
    pointValues.length >= 3 && pointValues.every((value) => value === 0);
  const hasCompletePoints = pointValues.length >= 3 && !isDefaultZeroSet;
  const minTemperature = hasCompletePoints ? Math.min(...pointValues) : null;
  const maxTemperature = hasCompletePoints ? Math.max(...pointValues) : null;
  const temperatureDiff = hasCompletePoints
    ? maxTemperature - minTemperature
    : null;
  const selectedPointCount = pointRows.filter(
    (point) => point.isSelected,
  ).length;
  const canSelectCreateInspectionResult = selectedPointCount >= POINT_MIN;
  const effectiveCreateInspectionResult =
    canSelectCreateInspectionResult && shouldCreateInspectionResult;
  const createInspectionResultOptionId = `three-phase-create-inspection-result-${
    file?.upload_id ?? "current"
  }`;

  const handlePointLimitChange = (nextPointLimit) => {
    const normalizedPointLimit = clamp(nextPointLimit, POINT_MIN, POINT_MAX);
    setPointLimit(normalizedPointLimit);
    setCircles((prev) => normalizePointSlots(prev, normalizedPointLimit));
  };

  const handlePointRemove = (pointIndex) => {
    setCircles((prev) => {
      const updated = normalizePointSlots(prev, pointLimit).slice();
      updated.splice(pointIndex, 1);
      return compactPointSlots(updated, pointLimit);
    });
  };

  const handleAreaRemoveFromList = (areaId) => {
    setAreas((prev) => prev.filter((area) => area.id !== areaId));
  };

  const handleCreateInspectionResultChange = (event) => {
    setShouldCreateInspectionResult(
      setCreateInspectionResultPref(event.target.checked),
    );
  };

  const handleSaveThermalInfo = () => {
    onSaveThermalInfo?.({
      createInspectionResult: effectiveCreateInspectionResult,
    });
  };

  useEffect(() => {
    if (file?.thermal_palette != null) {
      setCurrentPaletteOptionNumber(file.thermal_palette);
    }
  }, [file?.thermal_palette, setCurrentPaletteOptionNumber]);

  useEffect(() => {
    const savedPoints = normalizeSavedClickPoints(file?.click_points);
    const savedPointLimit = clamp(
      savedPoints.length || POINT_MIN,
      POINT_MIN,
      POINT_MAX,
    );
    const savedAreas = restoreClickAreas(
      file?.click_areas,
      editorSize,
      editorSize,
    ).map((area) => {
      const hasSavedStats =
        Number.isFinite(area.stats?.max) &&
        Number.isFinite(area.stats?.min) &&
        Number.isFinite(area.stats?.avg);
      const calculatedStats = hasSavedStats
        ? null
        : calculateAreaStats(file, area, editorSize, sourceEditorSize);

      return calculatedStats ? { ...area, stats: calculatedStats } : area;
    });

    setPointLimit(savedPointLimit);
    setCircles(normalizePointSlots(savedPoints, savedPointLimit));
    setMode(MODE_POINT);
    setAreaShape("rectangle");
    setAreas(savedAreas);
  }, [
    file,
    file?.upload_id,
    file?.click_points,
    file?.click_areas,
    editorSize,
    sourceEditorSize,
    setCircles,
  ]);

  useEffect(() => {
    if (!markupCaptureRef) return undefined;

    markupCaptureRef.current = () =>
      captureThreePhaseMarkupImage({
        imageSrc: currentThermalImg || file?.image_thermal,
        circles,
        areas,
        displaySize: editorSize,
      });

    return () => {
      markupCaptureRef.current = null;
    };
  }, [
    markupCaptureRef,
    currentThermalImg,
    file?.image_thermal,
    circles,
    areas,
    editorSize,
  ]);

  useEffect(() => {
    if (typeof onAreaCountChange === "function") {
      onAreaCountChange(areas.length);
    }
  }, [areas, onAreaCountChange]);

  return (
    <div className="threePhasePanel fileTabSection">
      <div className="threePhaseHero fileTabHeader">
        <div className="fileTabHeader__copy">
          <h3>{t("imageFileIndex.threePhasePanel.title")}</h3>
          <p>{t("imageFileIndex.threePhasePanel.description")}</p>
        </div>
        <span
          className={`threePhaseStatusPill fileTabHeader__status ${
            hasCompletePoints ? "is-complete" : "is-waiting"
          }`}
        >
          {hasCompletePoints
            ? t("imageFileIndex.threePhasePanel.complete")
            : t("imageFileIndex.threePhasePanel.incomplete")}
        </span>
      </div>

      <div className="threePhaseKpiGrid">
        <ThreePhaseKpi
          label={t("imageFileIndex.threePhasePanel.temperatureDiff")}
          icon={Thermometer}
          value={
            hasCompletePoints
              ? formatTemperature(temperatureDiff)
              : t("imageFileIndex.threePhasePanel.waiting")
          }
          highlight={hasCompletePoints}
        />
        <ThreePhaseKpi
          label={t("imageFileIndex.threePhasePanel.maxTemperature")}
          icon={ArrowUp}
          value={
            hasCompletePoints
              ? formatTemperature(maxTemperature)
              : t("imageFileIndex.threePhasePanel.waiting")
          }
        />
        <ThreePhaseKpi
          label={t("imageFileIndex.threePhasePanel.minTemperature")}
          icon={ArrowDown}
          value={
            hasCompletePoints
              ? formatTemperature(minTemperature)
              : t("imageFileIndex.threePhasePanel.waiting")
          }
        />
      </div>

      <div className="threePhaseWorkspace">
        <section className="threePhaseImagePanel" style={{
          "--three-phase-width": `${editorSize.width}px`,
          "--three-phase-height": `${editorSize.height}px`,
        }}>
          <div className="threePhaseSectionHeader">
            <div>
              <p className="threePhaseSectionTitle">
                <Image aria-hidden="true" />
                {t("imageFileIndex.subTitle.tripleSpot")}
              </p>
              <span>{t("imageFileIndex.threePhasePanel.pointGuide")}</span>
            </div>
            <div className="threePhaseSectionHeaderActions">
              {showSaveThermalButton &&
                typeof onSaveThermalInfo === "function" && (
                  <>
                    <label
                      className={`inspectionResultGenerationOption${
                        canSelectCreateInspectionResult ? "" : " is-disabled"
                      }`}
                      htmlFor={createInspectionResultOptionId}
                    >
                      <input
                        id={createInspectionResultOptionId}
                        type="checkbox"
                        checked={effectiveCreateInspectionResult}
                        onChange={handleCreateInspectionResultChange}
                        disabled={
                          !canSelectCreateInspectionResult || imgChangeLoading
                        }
                      />
                      <span>
                        {t(
                          "imageFileIndex.threePhasePanel.createInspectionResult",
                          {
                            defaultValue: "점검 결과 생성",
                          },
                        )}
                      </span>
                    </label>
                    <button
                      type="button"
                      className="btn-bg-blue"
                      onClick={handleSaveThermalInfo}
                      disabled={imgChangeLoading}
                    >
                      {saveThermalButtonLabel ||
                        t("imageFileIndex.buttons.thermalSave")}
                    </button>
                  </>
                )}
              <SingleImageDownloadBtn
                captureImage={async () => (await captureThreePhaseMarkupImage({
                  imageSrc, circles, areas, displaySize: editorSize,
                })).dataUrl}
                fileName={`${file?.clean_file_name}_triplespot`}
                captureTargetRef={tripleSpotCaptureTarget}
                t={t("imageFileIndex.buttons.singleImageDownload")}
              />
            </div>
          </div>
          <div className="threePhaseStageViewport">
            <div
              ref={tripleSpotCaptureTarget}
              className="threePhaseStage"
            >
              <ThreePhaseAnalysisOverlay
                file={file}
                circles={circles}
                setCircles={setCircles}
                editorSize={editorSize}
                sourceEditorSize={sourceEditorSize}
                pointLimit={pointLimit}
                mode={mode}
                areaShape={areaShape}
                areas={areas}
                setAreas={setAreas}
                t={t}
              />
              <img
                className="imgEditorMainImage"
                src={currentThermalImg || file?.image_thermal}
                alt=""
              />
            </div>
          </div>
          <ThreePhaseToolbar
            t={t}
            mode={mode}
            setMode={setMode}
            pointLimit={pointLimit}
            onPointLimitChange={handlePointLimitChange}
            selectedPointCount={selectedPointCount}
            onClearPoints={() =>
              setCircles(Array.from({ length: pointLimit }, () => undefined))
            }
            areaShape={areaShape}
            setAreaShape={setAreaShape}
            areasCount={areas.length}
            onClearAreas={() => setAreas([])}
          />
        </section>

        <div className="threePhaseSidePanel">
          <section className="threePhasePointPanel">
            <div className="threePhaseSectionHeader">
              <div>
                <p className="threePhaseSectionTitle">
                  <MapPin aria-hidden="true" />
                  {t("imageFileIndex.threePhasePanel.selectedPoints")}
                </p>
                <span>
                  {hasCompletePoints
                    ? `${t(
                        "imageFileIndex.threePhasePanel.temperatureDiff",
                      )} ${formatTemperature(temperatureDiff)}`
                    : t("imageFileIndex.threePhasePanel.notEnoughPoints")}
                </span>
              </div>
            </div>
            <div className="threePhasePointList">
              {pointRows.map((point, index) => (
                <div
                  className={`threePhasePointRow ${
                    point.isSelected ? "is-selected" : ""
                  }`}
                  key={point.key}
                >
                  <strong>{point.label}</strong>
                  <span>{point.displayValue}</span>
                  <small>
                    {t("imageFileIndex.threePhasePanel.positionLabel")}{" "}
                    {point.position}
                  </small>
                  <button
                    type="button"
                    className="threePhaseRowRemoveButton"
                    onClick={() => handlePointRemove(index)}
                    disabled={!point.isSelected}
                    aria-label={t(
                      "imageFileIndex.threePhasePanel.removePoint",
                      {
                        label: point.label,
                        defaultValue: "{{label}} 제거",
                      },
                    )}
                    title={t("imageFileIndex.threePhasePanel.removePoint", {
                      label: point.label,
                      defaultValue: "{{label}} 제거",
                    })}
                  >
                    <Trash2 aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section className="threePhaseAreaPanel">
            <div className="threePhaseSectionHeader">
              <div>
                <p className="threePhaseSectionTitle">
                  <Scan aria-hidden="true" />
                  {t("imageFileIndex.threePhasePanel.selectedAreas")}
                </p>
                <span>
                  {areas.length > 0
                    ? `${t(
                        "imageFileIndex.threePhasePanel.areaCount",
                      )} ${areas.length}/${AREA_MAX}`
                    : t("imageFileIndex.threePhasePanel.noSelectedAreas")}
                </span>
              </div>
            </div>
            <div className="threePhaseSelectedAreaList">
              {areas.length > 0 ? (
                areas.map((area, index) => (
                  <div className="threePhaseSelectedAreaRow" key={area.id}>
                    <strong>{`A${index + 1}`}</strong>
                    <div>
                      <span>
                        {t(`imageFileIndex.threePhasePanel.${area.shape}`)}
                      </span>
                      <small>
                        {`${Math.round(area.x)}, ${Math.round(
                          area.y,
                        )} / ${Math.round(area.width)} x ${Math.round(
                          area.height,
                        )}`}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="threePhaseRowRemoveButton"
                      onClick={() => handleAreaRemoveFromList(area.id)}
                      aria-label={t(
                        "imageFileIndex.threePhasePanel.removeArea",
                        {
                          label: `A${index + 1}`,
                          defaultValue: "{{label}} 제거",
                        },
                      )}
                      title={t("imageFileIndex.threePhasePanel.removeArea", {
                        label: `A${index + 1}`,
                        defaultValue: "{{label}} 제거",
                      })}
                    >
                      <Trash2 aria-hidden="true" />
                    </button>
                    <dl>
                      <div>
                        <dt>{t("imageFileIndex.threePhasePanel.areaMax")}</dt>
                        <dd>{formatTemperature(area.stats.max)}</dd>
                      </div>
                      <div>
                        <dt>{t("imageFileIndex.threePhasePanel.areaMin")}</dt>
                        <dd>{formatTemperature(area.stats.min)}</dd>
                      </div>
                      <div>
                        <dt>{t("imageFileIndex.threePhasePanel.areaAvg")}</dt>
                        <dd>{formatTemperature(area.stats.avg)}</dd>
                      </div>
                    </dl>
                  </div>
                ))
              ) : (
                <p className="threePhaseAreaEmpty">
                  {t("imageFileIndex.threePhasePanel.noSelectedAreas")}
                </p>
              )}
            </div>
          </section>

          <section className="threePhasePalettePanel">
            <div className="threePhaseSectionHeader">
              <div>
                <p className="threePhaseSectionTitle">
                  <Palette aria-hidden="true" />
                  {t("imageFileIndex.threePhasePanel.paletteTitle")}
                </p>
                <span>
                  {t("imageFileIndex.threePhasePanel.paletteDescription")}
                </span>
              </div>
            </div>
            <PaletteOptionsList
              currentPaletteOptionNumber={currentPaletteOptionNumber}
              setCurrentPaletteOptionNumber={setCurrentPaletteOptionNumber}
              upload_id={file?.upload_id}
              changing={changing}
              setChanging={setChanging}
              setCurrentThermalImg={setCurrentThermalImg}
              imgChangeLoading={imgChangeLoading}
              setImgChangeLoading={setImgChangeLoading}
              showAlert={showAlert}
              t={t}
            />
          </section>
        </div>
      </div>
    </div>
  );
}

export function ThreePhaseAnalysisOverlay({
  file,
  circles,
  setCircles,
  editorSize,
  sourceEditorSize,
  pointLimit,
  mode,
  areaShape,
  areas,
  setAreas,
  t,
}) {
  const overlayRef = useRef(null);
  const pointRefs = useRef([]);
  const previousPointCountRef = useRef(0);
  const draggingPointRef = useRef(null);
  const draggingAreaRef = useRef(null);
  const suppressAreaClickRef = useRef(false);
  const [draftArea, setDraftArea] = useState(null);
  const [draggingPointIndex, setDraggingPointIndex] = useState(null);
  const [draggingAreaId, setDraggingAreaId] = useState(null);
  const [selectedPointIndex, setSelectedPointIndex] = useState(null);

  useEffect(() => {
    setSelectedPointIndex(null);
  }, [file?.upload_id, mode, pointLimit]);

  useEffect(() => {
    const count = circles?.filter(Boolean).length ?? 0;
    if (count < previousPointCountRef.current || !circles?.[selectedPointIndex]) {
      setSelectedPointIndex(null);
    }
    previousPointCountRef.current = count;
  }, [circles, selectedPointIndex]);

  useEffect(() => {
    if (selectedPointIndex !== null) {
      pointRefs.current[selectedPointIndex]?.focus({ preventScroll: true });
    }
  }, [selectedPointIndex]);

  const handlePointKeyDown = (event) => {
    if (
      mode !== MODE_POINT || selectedPointIndex === null ||
      event.altKey || event.ctrlKey || event.metaKey ||
      draggingPointRef.current || draggingAreaRef.current
    ) return;

    const delta = {
      ArrowLeft: [-1, 0], ArrowRight: [1, 0],
      ArrowUp: [0, -1], ArrowDown: [0, 1],
    }[event.key];
    const remove = event.key === "Delete" || event.key === "Backspace";
    if (!delta && !remove) return;

    event.preventDefault();
    event.stopPropagation();
    setCircles((prev) => {
      const point = prev?.[selectedPointIndex];
      if (!point) return prev;
      const updated = normalizePointSlots(prev, pointLimit).slice();
      if (remove) {
        updated.splice(selectedPointIndex, 1);
        return compactPointSlots(updated, pointLimit);
      }
      const next = moveThreePhasePoint(
        point, ...delta, file, editorSize, sourceEditorSize,
      );
      if (next === point) return prev;
      updated[selectedPointIndex] = next;
      return updated;
    });
    if (remove) {
      setSelectedPointIndex(null);
      overlayRef.current?.focus({ preventScroll: true });
    }
  };
  const normalizedDraft = draftArea
    ? normalizeArea(
        draftArea.startX,
        draftArea.startY,
        draftArea.currentX,
        draftArea.currentY,
        areaShape,
        editorSize,
      )
    : null;
  const draftAreaStats = useMemo(() => {
    if (
      !normalizedDraft ||
      normalizedDraft.width < 2 ||
      normalizedDraft.height < 2
    ) {
      return null;
    }

    return calculateAreaStats(
      file,
      normalizedDraft,
      editorSize,
      sourceEditorSize,
    );
  }, [file, normalizedDraft, editorSize, sourceEditorSize]);

  const addPointAtPosition = (x, y) => {
    const value = getCsvTemperature(file, x, y, editorSize, sourceEditorSize);

    if (value === null) return false;

    const nextIndex = normalizePointSlots(circles, pointLimit).findIndex((point) => !point);
    if (nextIndex === -1) return false;

    setCircles((prev) => {
      const normalized = normalizePointSlots(prev, pointLimit);
      const nextIndex = normalized.findIndex((point) => !point);

      if (nextIndex === -1) return prev;

      const updated = [...normalized];
      updated[nextIndex] = {
        x,
        y,
        value: value.toFixed(1),
      };

      return updated;
    });
    setSelectedPointIndex(nextIndex);
    return true;
  };

  const updatePointAtPosition = (index, x, y) => {
    const clampedX = clamp(x, 0, editorSize.width);
    const clampedY = clamp(y, 0, editorSize.height);
    const value = getCsvTemperature(
      file,
      clampedX,
      clampedY,
      editorSize,
      sourceEditorSize,
    );

    if (value === null) return;

    setCircles((prev) => {
      const updated = normalizePointSlots(prev, pointLimit).slice();
      updated[index] = {
        x: clampedX,
        y: clampedY,
        value: value.toFixed(1),
      };
      return updated;
    });
  };

  const handlePointPointerDown = (event, index) => {
    if (mode !== MODE_POINT) return;

    event.preventDefault();
    event.stopPropagation();

    const overlay = overlayRef.current;
    if (!overlay) return;

    setSelectedPointIndex(index);
    pointRefs.current[index]?.focus({ preventScroll: true });

    const { x, y } = getPointerPositionFromElement(event, overlay, editorSize);
    overlay.setPointerCapture?.(event.pointerId);
    draggingPointRef.current = {
      index,
      pointerId: event.pointerId,
      startX: x,
      startY: y,
      hasMoved: false,
    };
    setDraggingPointIndex(index);
  };

  const handleAreaPointerDown = (event, area) => {
    if (mode !== MODE_AREA) return;

    event.preventDefault();
    event.stopPropagation();

    const overlay = overlayRef.current;
    if (!overlay) return;

    const { x, y } = getPointerPositionFromElement(event, overlay, editorSize);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    draggingAreaRef.current = {
      id: area.id,
      pointerId: event.pointerId,
      startX: x,
      startY: y,
      offsetX: x - area.x,
      offsetY: y - area.y,
      width: area.width,
      height: area.height,
      shape: area.shape,
      hasMoved: false,
    };
    setDraggingAreaId(area.id);
  };

  const handlePointerDown = (event) => {
    if (mode !== MODE_AREA || areas.length >= AREA_MAX) return;

    const { x, y } = getPointerPosition(event, editorSize);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraftArea({
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
    });
  };

  const handlePointerMove = (event) => {
    if (draggingPointRef.current) {
      const overlay = overlayRef.current;
      if (!overlay) return;

      const { x, y } = getPointerPositionFromElement(event, overlay, editorSize);
      const dragInfo = draggingPointRef.current;
      const movedDistance = Math.hypot(
        x - dragInfo.startX,
        y - dragInfo.startY,
      );

      if (movedDistance > 2) {
        dragInfo.hasMoved = true;
      }

      updatePointAtPosition(dragInfo.index, x, y);
      return;
    }

    if (draggingAreaRef.current) {
      const overlay = overlayRef.current;
      if (!overlay) return;

      const { x, y } = getPointerPositionFromElement(event, overlay, editorSize);
      const dragInfo = draggingAreaRef.current;
      const movedDistance = Math.hypot(
        x - dragInfo.startX,
        y - dragInfo.startY,
      );

      if (movedDistance > 2) {
        dragInfo.hasMoved = true;
      }

      const nextX = clamp(
        x - dragInfo.offsetX,
        0,
        editorSize.width - dragInfo.width,
      );
      const nextY = clamp(
        y - dragInfo.offsetY,
        0,
        editorSize.height - dragInfo.height,
      );
      const nextArea = {
        x: nextX,
        y: nextY,
        width: dragInfo.width,
        height: dragInfo.height,
        shape: dragInfo.shape,
      };
      const stats = calculateAreaStats(
        file,
        nextArea,
        editorSize,
        sourceEditorSize,
      );

      setAreas((prev) =>
        prev.map((area) =>
          area.id === dragInfo.id
            ? {
                ...area,
                ...nextArea,
                stats: stats ?? area.stats,
              }
            : area,
        ),
      );

      if (dragInfo.hasMoved) {
        suppressAreaClickRef.current = true;
      }

      return;
    }

    if (!draftArea) return;

    const { x, y } = getPointerPosition(event, editorSize);
    setDraftArea((prev) =>
      prev
        ? {
            ...prev,
            currentX: x,
            currentY: y,
          }
        : prev,
    );
  };

  const finishPointDrag = () => {
    const dragInfo = draggingPointRef.current;

    if (!dragInfo) return false;

    draggingPointRef.current = null;
    setDraggingPointIndex(null);

    return true;
  };

  const finishAreaDrag = () => {
    const dragInfo = draggingAreaRef.current;

    if (!dragInfo) return false;

    draggingAreaRef.current = null;
    setDraggingAreaId(null);

    if (dragInfo.hasMoved) {
      suppressAreaClickRef.current = true;
    }

    return true;
  };

  const finishArea = (event) => {
    if (finishPointDrag()) return;
    if (finishAreaDrag()) return;

    if (mode === MODE_POINT) {
      if (!event || event.target !== event.currentTarget) return;

      const { x, y } = getPointerPosition(event, editorSize);
      addPointAtPosition(x, y);
      return;
    }

    if (!draftArea) return;

    const area = normalizeArea(
      draftArea.startX,
      draftArea.startY,
      draftArea.currentX,
      draftArea.currentY,
      areaShape,
      editorSize,
    );

    setDraftArea(null);

    if (area.width < 8 || area.height < 8 || areas.length >= AREA_MAX) return;

    const stats = calculateAreaStats(file, area, editorSize, sourceEditorSize);

    if (!stats) return;

    setAreas((prev) => [
      ...prev,
      {
        ...area,
        id: `${Date.now()}-${prev.length}`,
        stats,
      },
    ]);
  };

  const handleAreaRemove = (event, areaId) => {
    if (mode !== MODE_AREA) return;

    event.preventDefault();
    event.stopPropagation();

    if (suppressAreaClickRef.current) {
      suppressAreaClickRef.current = false;
      return;
    }

    setAreas((prev) => prev.filter((area) => area.id !== areaId));
  };

  const handlePointRemove = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    if (mode !== MODE_POINT || draggingPointRef.current) return;

    setCircles((prev) => {
      const updated = normalizePointSlots(prev, pointLimit).slice();
      updated.splice(index, 1);
      return compactPointSlots(updated, pointLimit);
    });
    setSelectedPointIndex(null);
    overlayRef.current?.focus({ preventScroll: true });
  };

  return (
    <div
      ref={overlayRef}
      className={`threePhaseAnalysisOverlay ${
        mode === MODE_AREA ? "is-area-mode" : "is-point-mode"
      }`}
      role="group"
      tabIndex={mode === MODE_POINT ? 0 : -1}
      aria-label={t("imageFileIndex.threePhasePanel.selectedPoints")}
      onKeyDown={handlePointKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishArea}
      onPointerCancel={() => {
        draggingPointRef.current = null;
        draggingAreaRef.current = null;
        setDraggingPointIndex(null);
        setDraggingAreaId(null);
        setDraftArea(null);
      }}
    >
      {circles?.slice(0, pointLimit).map((circle, index) => {
        if (!circle) return null;
        const removePosition = getPointRemoveButtonPosition(circle, editorSize);
        return (
          <div
            key={`three-phase-point-${index}`}
            className={`threePhasePointGroup ${draggingPointIndex === index ? "is-dragging" : ""}`}
            style={{
              left: circle.x - 15,
              top: circle.y - 15,
              "--remove-left": `${removePosition.left}px`,
              "--remove-top": `${removePosition.top}px`,
            }}
          >
          <button
            type="button"
            ref={(element) => { pointRefs.current[index] = element; }}
            tabIndex={mode === MODE_POINT ? 0 : -1}
            aria-label={`L${index + 1}: ${circle.value} °C`}
            aria-pressed={mode === MODE_POINT && selectedPointIndex === index}
            onFocus={() => {
              if (mode === MODE_POINT) setSelectedPointIndex(index);
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (mode === MODE_POINT) setSelectedPointIndex(index);
            }}
            onPointerDown={(event) => handlePointPointerDown(event, index)}
            className={`threePhasePointMarker ${
              shouldPlacePointLabelBelow(circle.y) ? "is-label-below" : ""
            } ${
              draggingPointIndex === index ? "is-dragging" : ""
            }`}
            style={{
              left: 0,
              top: 0,
            }}
            data-value={`L${index + 1}: ${circle.value}`}
          >
            {mode === MODE_POINT && selectedPointIndex === index && (
              <span
                className="threePhasePointSelectionRing"
                data-html2canvas-ignore="true"
                aria-hidden="true"
              />
            )}
          </button>
          {mode === MODE_POINT && (
            <button
              type="button"
              className="threePhasePointRemoveButton"
              style={removePosition}
              data-html2canvas-ignore="true"
              aria-label={t("imageFileIndex.threePhasePanel.removePoint", {
                label: `L${index + 1}`, defaultValue: "{{label}} 제거",
              })}
              onPointerDown={(event) => event.stopPropagation()}
              onPointerUp={(event) => event.stopPropagation()}
              onClick={(event) => handlePointRemove(event, index)}
            >
              <Trash2 aria-hidden="true" />
            </button>
          )}
          </div>
        );
      })}

      {areas.map((area, index) => (
        <div className="threePhaseAreaLayer" key={area.id}>
          <div
            className={`threePhaseAreaShape is-${area.shape} ${
              draggingAreaId === area.id ? "is-dragging" : ""
            }`}
            onPointerDown={(event) => handleAreaPointerDown(event, area)}
            onClick={(event) => handleAreaRemove(event, area.id)}
            style={{
              left: area.x,
              top: area.y,
              width: area.width,
              height: area.height,
            }}
          />
          <div
            className={`threePhaseAreaStats ${getAreaLabelClassName(
              area,
              editorSize,
            )}`}
            onPointerDown={(event) => handleAreaPointerDown(event, area)}
            onClick={(event) => handleAreaRemove(event, area.id)}
            style={getAreaLabelStyle(area, editorSize)}
          >
            <strong>{`A${index + 1}`}</strong>
            <span>Max {formatAreaStatsTemperature(area.stats.max)}</span>
            <span>Min {formatAreaStatsTemperature(area.stats.min)}</span>
            <span>Avg {formatAreaStatsTemperature(area.stats.avg)}</span>
          </div>
        </div>
      ))}

      {normalizedDraft && (
        <>
          <div
            className={`threePhaseAreaShape is-draft is-${normalizedDraft.shape}`}
            style={{
              left: normalizedDraft.x,
              top: normalizedDraft.y,
              width: normalizedDraft.width,
              height: normalizedDraft.height,
            }}
          />
          {draftAreaStats && (
            <div
              className={`threePhaseAreaStats is-draft ${getAreaLabelClassName(
                normalizedDraft,
                editorSize,
              )}`}
              style={getAreaLabelStyle(normalizedDraft, editorSize)}
            >
              <strong>{`A${areas.length + 1}`}</strong>
              <span>Max {formatAreaStatsTemperature(draftAreaStats.max)}</span>
              <span>Min {formatAreaStatsTemperature(draftAreaStats.min)}</span>
              <span>Avg {formatAreaStatsTemperature(draftAreaStats.avg)}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ThreePhaseToolbar({
  t,
  mode,
  setMode,
  pointLimit,
  onPointLimitChange,
  selectedPointCount,
  onClearPoints,
  areaShape,
  setAreaShape,
  areasCount,
  onClearAreas,
}) {
  return (
    <div className="threePhaseToolbar">
      <div className="threePhaseToolbarSection is-mode">
        <span className="threePhaseToolbarTitle">
          <SlidersHorizontal aria-hidden="true" />
          {t("imageFileIndex.threePhasePanel.mode")}
        </span>
        <div className="threePhaseToolbarControls">
          <div className="threePhaseSegmented is-modeSwitch">
            <button
              type="button"
              className={mode === MODE_POINT ? "is-active" : ""}
              onClick={() => setMode(MODE_POINT)}
            >
              <MousePointer2 aria-hidden="true" />
              {t("imageFileIndex.threePhasePanel.pointMode")}
            </button>
            <button
              type="button"
              className={mode === MODE_AREA ? "is-active" : ""}
              onClick={() => setMode(MODE_AREA)}
            >
              <Scan aria-hidden="true" />
              {t("imageFileIndex.threePhasePanel.areaMode")}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`threePhaseToolbarSection${mode !== MODE_POINT ? " is-muted" : ""}`}
      >
        <span className="threePhaseToolbarTitle">
          <MapPin aria-hidden="true" />
          {t("imageFileIndex.threePhasePanel.pointMode")}
        </span>
        <div className="threePhaseToolbarControls">
          <span className="threePhaseToolbarMeta">
            {`${t("imageFileIndex.threePhasePanel.pointCount")} ${selectedPointCount}/${pointLimit}`}
          </span>
          <div className="threePhaseStepper">
            <button
              type="button"
              onClick={() => onPointLimitChange(pointLimit - 1)}
              disabled={pointLimit <= POINT_MIN}
              aria-label={t("imageFileIndex.threePhasePanel.decreasePoint")}
            >
              <Minus aria-hidden="true" />
            </button>
            <strong>{pointLimit}</strong>
            <button
              type="button"
              onClick={() => onPointLimitChange(pointLimit + 1)}
              disabled={pointLimit >= POINT_MAX}
              aria-label={t("imageFileIndex.threePhasePanel.increasePoint")}
            >
              <Plus aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            className="threePhaseClearButton"
            onClick={onClearPoints}
            disabled={selectedPointCount === 0}
          >
            <Trash2 aria-hidden="true" />
            {t("imageFileIndex.threePhasePanel.clearPoints")}
          </button>
        </div>
      </div>

      <div
        className={`threePhaseToolbarSection is-area${mode !== MODE_AREA ? " is-muted" : ""}`}
      >
        <span className="threePhaseToolbarTitle">
          <Scan aria-hidden="true" />
          {t("imageFileIndex.threePhasePanel.areaMode")}
        </span>
        <div className="threePhaseToolbarControls">
          <span className="threePhaseToolbarMeta">
            {`${t("imageFileIndex.threePhasePanel.areaCount")} ${areasCount}/${AREA_MAX}`}
          </span>
          <div className="threePhaseSegmented is-shapeSwitch">
            {AREA_SHAPES.map((shape) => {
              const ShapeIcon = AREA_SHAPE_ICONS[shape];

              return (
                <button
                  type="button"
                  className={areaShape === shape ? "is-active" : ""}
                  onClick={() => setAreaShape(shape)}
                  disabled={mode !== MODE_AREA}
                  key={shape}
                >
                  <ShapeIcon aria-hidden="true" />
                  {t(`imageFileIndex.threePhasePanel.${shape}`)}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="threePhaseClearButton"
            onClick={onClearAreas}
            disabled={areasCount === 0}
          >
            <Trash2 aria-hidden="true" />
            {t("imageFileIndex.threePhasePanel.clearAreas")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ThreePhaseKpi({ label, value, highlight = false, icon: Icon = null }) {
  return (
    <div className={`threePhaseKpi${highlight ? " is-highlight" : ""}`}>
      <span>
        {Icon ? <Icon aria-hidden="true" /> : null}
        {label}
      </span>
      <strong>{value}</strong>
    </div>
  );
}
